import { useAppSelector } from '@/state/hooks';
import { selectCredentials, selectCurrentAccount } from '@/state/reducers/authSlice';
import { useSso } from '@/utils/providers/SSOProvider';
import { jwtDecode } from 'jwt-decode';
import { useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Logger } from '../logger';

export interface UsePortalAuthenticationReturn {
    // on401Error: (error: any, callback: () => void) => Promise<void>;
}

const usePortalAuthentication = (): UsePortalAuthenticationReturn => {
    const { jwt, isLoadingSSO, isSSOEnabled, onFetchJWT } = useSso();

    const navigate = useNavigate();
    const credentials = useAppSelector(selectCredentials);
    const currentAccount = useAppSelector(selectCurrentAccount);

    const onRefreshSession = useCallback(() => {
        Logger({
            title: 'Session has expired, attempting to refresh Session or redirect to login',
            callback: () => {}
        });
        onFetchJWT();
    }, [onFetchJWT]);

    useEffect(() => {
        if (!jwt) return;

        let decoded;
        try {
            decoded = jwtDecode(jwt);
        } catch (e) {
            Logger({
                title: 'Invalid JWT detected, redirecting to login',
                callback: () => {}
            });
            navigate('/login');
            return;
        }

        const expTime = decoded.exp * 1000;

        const checkExpiration = () => {
            const now = Date.now();
            const timeLeft = expTime - now;
            if (timeLeft <= 60 * 1000) {
                onRefreshSession();
            }
        };

        // Check every 30s
        // Also run once immediately
        checkExpiration();

        const interval = setInterval(checkExpiration, 30 * 1000);

        // const activityEvents = ['keypress', 'click', 'mousemove'];
        // const handleActivity = () => checkExpiration();

        // activityEvents.forEach((evt) => {
        //     window.addEventListener(evt, handleActivity);
        // });
        return () => {
            clearInterval(interval);
            // activityEvents.forEach((evt) => {
            //     window.removeEventListener(evt, handleActivity);
            // });
        };
    }, [jwt, onRefreshSession]);

    useEffect(() => {
        if (!isSSOEnabled || !jwt || !credentials) {
            navigate('/login');
            Logger({
                title: 'Redirecting to login due to SSO not enabled or missing JWT/credentials',
                callback: () => {}
            });
        }
    }, [jwt, isLoadingSSO, isSSOEnabled, credentials, currentAccount]);

    return {};
};

export default usePortalAuthentication;

export const use401Error = () => {
    const { onFetchJWT } = useSso();
    const navigate = useNavigate();
    const credentials = useAppSelector(selectCredentials);

    const on401Error = useCallback(
        async (error, callback) => {
            if (error?.response?.status === 401) {
                callback();

                // Attempt to refresh JWT or redirect to login
                Logger({
                    title: 'Session has expired, attempting to refresh Session or redirect to login',
                    callback: () => {}
                });

                if (!credentials) {
                    Logger({
                        title: 'No auth token found, redirecting to login...',
                        callback: () => {}
                    });
                    navigate('/login');
                } else {
                    try {
                        const jwt = await onFetchJWT();
                        if (jwt) {
                            Logger({
                                title: 'JWT refreshed successfully',
                                callback: () => {}
                            });
                        } else {
                            Logger({
                                title: 'Failed to refresh JWT, redirecting to login...',
                                callback: () => {}
                            });
                            navigate('/login');
                        }
                    } catch (error) {
                        Logger({
                            title: 'Failed to refresh JWT, redirecting to login...',
                            callback: () => {}
                        });
                        navigate('/login');
                    }
                }
            }
        },
        [onFetchJWT, navigate, credentials]
    );

    return { on401Error };
};
